import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { channelWa } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { Filter, SearchField } from '../../../components/panel';

/**
 * The library, browsable.
 *
 * Two sources arrive in one list and are told apart by a badge, because
 * "Meta's own wording, already reviewed" and "wording we wrote for you" are
 * different claims and passing one off as the other would be a lie the
 * customer only discovers at review time.
 *
 * When Meta's library is not open to this account, that is said in one plain
 * sentence rather than left as a silently shorter list.
 */
export function LibraryBrowser({
  onChoose,
  chosenKey
}: {
  onChoose: (entry: channelWa.TemplateLibraryEntry) => void;
  chosenKey?: string | null;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const search = useDebounce(query, 250);

  const { data, isPending, error } = useQuery({
    queryKey: ['template-library', search, category],
    queryFn: () => channelWa.templateLibrary({
      search: search.trim() || undefined,
      category: category || undefined
    })
  });

  const entries = data?.data ?? [];
  const metaAvailable = data?.meta?.meta_available ?? false;

  return (
    <Stack gap="md">
      <div className="rr-panel__filters rr-tplwiz__libfilters">
        <Filter label={t('templates.library.search.label')}>
          <SearchField>
            <TextInput
              type="search"
              className="rr-panel__search-input"
              placeholder={t('templates.library.search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
            />
          </SearchField>
        </Filter>
        <Filter label={t('templates.columns.category')}>
          <Select
            value={category}
            onChange={(e) => setCategory(e.currentTarget.value)}
            data={[
              { value: '', label: t('templates.library.category.any') },
              { value: 'utility', label: t('templates.category.utility') },
              { value: 'marketing', label: t('templates.category.marketing') },
              { value: 'authentication', label: t('templates.category.authentication') }
            ]}
          />
        </Filter>
      </div>

      {error && <Alert color="error" role="alert">{t('templates.library.loadError')}</Alert>}

      {/* Not a failure. Meta opens this edge to some apps and accounts and not
          others, and a customer is owed the reason their list is short. */}
      {!isPending && !error && !metaAvailable && (
        <Alert color="info">{t('templates.library.metaUnavailable')}</Alert>
      )}

      {isPending ? (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
      ) : entries.length === 0 ? (
        <EmptyState
          title={t('templates.library.empty')}
          description={t('templates.library.empty.hint')}
        />
      ) : (
        <div className="rr-tplwiz__cards" role="list">
          {entries.map((entry) => (
            <LibraryCard
              key={entry.key}
              entry={entry}
              chosen={entry.key === chosenKey}
              onChoose={() => onChoose(entry)}
            />
          ))}
        </div>
      )}
    </Stack>
  );
}

function LibraryCard({
  entry,
  chosen,
  onChoose
}: {
  entry: channelWa.TemplateLibraryEntry;
  chosen: boolean;
  onChoose: () => void;
}) {
  const t = useT();
  return (
    <div className={`rr-tplwiz__card${chosen ? ' is-chosen' : ''}`} role="listitem">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text role="body" fw={650}>{entry.title}</Text>
        <Badge size="xs" variant="light" color={entry.source === 'meta' ? 'blue' : 'gray'}>
          {entry.source === 'meta' ? t('templates.library.source.meta') : t('templates.library.source.starter')}
        </Badge>
      </Group>

      {entry.purpose && (
        <Text role="caption" c="var(--rr-color-text-muted)">{entry.purpose}</Text>
      )}

      <div className="rr-tplwiz__card-body">
        {entry.header && <div className="rr-tplwiz__card-header">{entry.header}</div>}
        <div>{entry.body}</div>
        {entry.footer && <div className="rr-tplwiz__card-footer">{entry.footer}</div>}
      </div>

      <Group gap={6} wrap="wrap">
        <Badge size="xs" variant="light">{t(`templates.category.${entry.category}`, entry.category)}</Badge>
        {entry.usecase && <Badge size="xs" variant="light">{humanise(entry.usecase)}</Badge>}
      </Group>

      <Button size="xs" variant={chosen ? 'filled' : 'default'} onClick={onChoose}>
        {chosen ? t('templates.library.chosen') : t('templates.library.use')}
      </Button>
    </div>
  );
}

/** `DELIVERY_UPDATE` → `Delivery update`. Meta's own vocabulary, made readable. */
function humanise(value: string): string {
  const words = value.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
